/**
 * smokeTests.js
 *
 * Registry of every download endpoint in Doctivity.
 * Each entry describes one smoke test: what to call, how to call it,
 * and what context it requires.
 *
 * The runner in SmokeTestCard resolves `context` before executing tests:
 *   context = {
 *     clientId,          // app.selectedClient
 *     providerId,        // first affiliated provider id for the client
 *     organizationId,    // first org associated with the client
 *     locationId,        // primary location of the context provider
 *     servicelineId,     // "General Medicine Dx" id, or first available
 *     tagId,             // first label/tag for the client
 *     dateFrom,          // app.dateRange.start (ISO string)
 *     dateTo,            // app.dateRange.end (ISO string)
 *   }
 *
 * Each test:
 *   id             - unique string key
 *   name           - human-readable label shown in the results table
 *   path(context)  - function returning the app path where this download lives
 *   requiresClaims - if true, skip when client lacks has_claims_access
 *   run(axiosInstance, context) => Promise<{ data, headers }>
 *                    Should throw on failure; resolve with response for pass.
 */

// ── Provider tests ────────────────────────────────────────────────────────────
// Includes client-wide downloads that live on provider-related pages.

const providerTests = [
  {
    id: 'affiliated_orgs',
    name: 'Affiliated Orgs Download',
    path: () => '/documents/data',
    requiresClaims: false,
    run: (axios, ctx) =>
      axios.get(`/AffiliatedOrganizationsDownload/${ctx.clientId}`),
  },
  {
    id: 'affiliated_providers',
    name: 'Affiliated Providers Download',
    path: () => '/documents/data',
    requiresClaims: false,
    run: (axios, ctx) =>
      axios.get(`/AffiliatedProvidersDownload/${ctx.clientId}`),
  },
  {
    id: 'contacts',
    name: 'Contacts Download',
    path: () => '/documents/data',
    requiresClaims: false,
    run: (axios, ctx) => axios.get(`/ContactsDownload/${ctx.clientId}`),
  },
  {
    id: 'activity_notes',
    name: 'Activity Notes Download',
    path: () => '/documents/data',
    requiresClaims: false,
    run: (axios, ctx) => axios.get(`/ActivityNotesDownload/${ctx.clientId}`),
  },
  {
    id: 'provider_addresses',
    name: 'Provider Mailing Addresses',
    path: () => '/documents/data',
    requiresClaims: false,
    run: (axios, ctx) =>
      axios.get('/Providers', {
        params: {
          opts: {
            where: {},
            include: [
              { association: 'clients', attributes: ['id', 'name'] },
              { association: 'taxonomy' },
              { association: 'location' },
            ],
            order: [['id', 'ASC']],
            limit: 25,
            offset: 0,
          },
        },
      }),
  },
  {
    id: 'provider_visits',
    name: 'Provider Visits CSV',
    path: (ctx) => `/providers/${ctx.providerId}/visits`,
    requiresClaims: false,
    run: (axios, ctx) =>
      axios.get('/Visits', {
        params: {
          opts: JSON.stringify({
            where: { client_id: ctx.clientId },
            format: 'csv',
            limit: 25,
            offset: 0,
          }),
          selected_client_id: ctx.clientId,
        },
      }),
  },
  {
    id: 'referrals_in',
    name: 'Provider Referrals In CSV',
    path: (ctx) => `/providers/${ctx.providerId}/visits`,
    requiresClaims: false,
    run: (axios, ctx) =>
      axios.post(
        '/Analytics',
        {
          type: 'VISIT_COUNT_BY_MONTH',
          format: 'csv',
          filter: {
            client_id: ctx.clientId,
            provider_id: ctx.providerId,
            from: ctx.dateFrom,
            to: ctx.dateTo,
          },
        },
        { params: { selected_client_id: ctx.clientId } },
      ),
  },
  {
    id: 'referrals_out',
    name: 'Provider Referrals Out CSV',
    path: (ctx) => `/providers/${ctx.providerId}/visits`,
    requiresClaims: false,
    run: (axios, ctx) =>
      axios.post(
        '/Analytics',
        {
          type: 'PROVIDER_REFERRED_OUT_COUNT_BY_MONTH',
          format: 'csv',
          filter: {
            client_id: ctx.clientId,
            referrer_id: ctx.providerId,
            from: ctx.dateFrom,
            to: ctx.dateTo,
          },
        },
        { params: { selected_client_id: ctx.clientId } },
      ),
  },
  {
    id: 'provider_pdf',
    name: 'Provider PDF Report',
    path: (ctx) => `/providers/${ctx.providerId}/scorecard`,
    requiresClaims: false,
    // Note: returns 500 when the test provider has no scorecard data — expected in dev.
    run: (axios, ctx) =>
      axios.post(
        '/PDF',
        {
          filter: {
            client_id: ctx.clientId,
            provider_id: ctx.providerId,
            from: ctx.dateFrom,
            to: ctx.dateTo,
          },
        },
        { params: { selected_client_id: ctx.clientId } },
      ),
  },
  {
    id: 'stark_export',
    name: 'Stark Law Export CSV',
    path: (ctx) => `/providers/${ctx.providerId}/stark`,
    requiresClaims: false,
    run: (axios, ctx) => {
      const params = new URLSearchParams({
        clientId: ctx.clientId,
        startDate: ctx.dateFrom,
        endDate: ctx.dateTo,
      });
      return axios.get(`/v2/stark/export?${params.toString()}`, { responseType: 'blob' });
    },
  },
  {
    id: 'mileage_export',
    name: 'Mileage Export CSV',
    path: (ctx) => `/providers/${ctx.providerId}/stark`,
    requiresClaims: false,
    run: (axios, ctx) =>
      axios.get('/v2/mileage/export', {
        params: { clientId: ctx.clientId, startDate: ctx.dateFrom, endDate: ctx.dateTo },
        responseType: 'blob',
      }),
  },
  {
    id: 'claims_payers',
    name: 'Claims Payers CSV',
    path: (ctx) => `/providers/${ctx.providerId}/market`,
    requiresClaims: true,
    run: (axios, ctx) =>
      axios.get('/ClaimsPayers', {
        params: {
          opts: {
            where: { provider_id: ctx.providerId },
            include: [{ association: 'payer', attributes: ['id', 'name', 'type'] }],
            order: [['percent', 'DESC']],
            limit: 25,
          },
        },
      }),
  },
  {
    id: 'claims_network',
    name: 'Claims Network CSV',
    path: (ctx) => `/providers/${ctx.providerId}/shared`,
    requiresClaims: true,
    run: (axios, ctx) =>
      axios.post(
        '/Analytics',
        {
          type: 'CLAIMS_NETWORK',
          format: 'csv',
          filter: {
            provider_id: ctx.providerId,
            client_id: ctx.clientId,
            serviceline: ctx.servicelineId,
            before: true,
          },
          opts: { limit: 25, offset: 0 },
        },
        { params: { selected_client_id: ctx.clientId } },
      ),
  },
  {
    id: 'captured_dx_codes',
    name: 'Captured Dx Codes CSV',
    path: (ctx) => `/providers/${ctx.providerId}/codes`,
    requiresClaims: true,
    run: (axios, ctx) =>
      axios.get('/ProviderDxCodeCounts', {
        params: { opts: { where: { provider_id: ctx.providerId }, limit: 25, offset: 0 } },
      }),
  },
  {
    id: 'captured_px_codes',
    name: 'Captured Px Codes CSV',
    path: (ctx) => `/providers/${ctx.providerId}/codes`,
    requiresClaims: true,
    run: (axios, ctx) =>
      axios.get('/ProviderPxCodeCounts', {
        params: { opts: { where: { provider_id: ctx.providerId }, limit: 25, offset: 0 } },
      }),
  },
];

// ── Organization tests ────────────────────────────────────────────────────────

const orgTests = [
  {
    id: 'claims_org',
    name: 'Claims Org Providers CSV',
    path: (ctx) => `/organizations/${ctx.organizationId}/market`,
    requiresClaims: true,
    run: (axios, ctx) =>
      axios.post(
        '/Analytics',
        {
          type: 'CLAIMS_IN_ORGANIZATION',
          format: 'csv',
          filter: { organization_id: ctx.organizationId, client_id: ctx.clientId },
          opts: { limit: 25, offset: 0 },
        },
        { params: { selected_client_id: ctx.clientId } },
      ),
  },
  {
    id: 'claims_org_payers',
    name: 'Claims Org Payers CSV',
    path: (ctx) => `/organizations/${ctx.organizationId}/market`,
    requiresClaims: true,
    run: (axios, ctx) =>
      axios.get('/OrgPayers', {
        params: {
          opts: {
            where: { organization_id: ctx.organizationId },
            include: [{ association: 'payer', attributes: ['id', 'name', 'type'] }],
            order: [['percent', 'DESC']],
            limit: 25,
          },
        },
      }),
  },
  {
    id: 'org_migration_county',
    name: 'Org Migration (County) CSV',
    path: (ctx) => `/organizations/${ctx.organizationId}/migration`,
    requiresClaims: false,
    run: (axios, ctx) =>
      axios.get('/PatientMigrationCounty', {
        params: {
          opts: {
            where: { organization_id: ctx.organizationId },
            include: [
              { attributes: ['id', 'name', 'parent_id'], association: 'serviceline', required: true, where: {} },
              { attributes: ['id', 'county_name', 'state', 'geo_id'], association: 'county', required: true },
            ],
            order: [['total_patients', 'DESC']],
            limit: 25,
            offset: 0,
          },
        },
      }),
  },
  {
    id: 'org_migration_zip3',
    name: 'Org Migration (Zip3) CSV',
    path: (ctx) => `/organizations/${ctx.organizationId}/migration`,
    requiresClaims: false,
    run: (axios, ctx) =>
      axios.get('/PatientMigrationZip3', {
        params: {
          opts: {
            where: { organization_id: ctx.organizationId },
            include: [
              { attributes: ['id', 'name', 'parent_id'], association: 'serviceline', required: true, where: {} },
            ],
            order: [['total_patients', 'DESC']],
            limit: 25,
            offset: 0,
          },
        },
      }),
  },
];

// ── Location tests ────────────────────────────────────────────────────────────
// Mirrors the two API calls made by downloadLocationPDF (locationPdfGenerator.js).

const locationTests = [
  {
    id: 'location_pdf_providers',
    name: 'Location PDF — Providers at Location',
    path: (ctx) => ctx.locationId ? `/locations/${ctx.locationId}` : null,
    requiresClaims: false,
    run: (axios, ctx) => {
      if (!ctx.locationId) throw new Error('No locationId in context');
      return axios.get('/Providers', {
        params: {
          opts: {
            where: {},
            attributes: ['id', 'first_name', 'last_name', 'credential', 'npi', 'phone', 'email'],
            include: [
              { association: 'location', where: { id: ctx.locationId }, required: true, attributes: [] },
              { association: 'taxonomy', attributes: ['id', 'classification', 'doctivity_name'] },
            ],
            order: [['last_name', 'ASC']],
            limit: 25,
            offset: 0,
          },
        },
      });
    },
  },
  {
    id: 'location_pdf_contacts',
    name: 'Location PDF — Contacts at Location',
    path: (ctx) => ctx.locationId ? `/locations/${ctx.locationId}` : null,
    requiresClaims: false,
    run: (axios, ctx) => {
      if (!ctx.locationId) throw new Error('No locationId in context');
      return axios.get('/Contacts', {
        params: {
          opts: {
            where: { client_id: ctx.clientId },
            attributes: ['id', 'first_name', 'last_name', 'title', 'phone', 'email'],
            include: [
              { association: 'location', where: { id: ctx.locationId }, required: true, attributes: [] },
            ],
            order: [['last_name', 'ASC']],
            limit: 25,
            offset: 0,
          },
        },
      });
    },
  },
];

const smokeTests = { provider: providerTests, org: orgTests, location: locationTests };

export { smokeTests, providerTests, orgTests, locationTests };
